import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { LoadingSpinner } from '../../components/common';
import { getProductImageUrl } from '../../utils/productHelpers';
import { formatCurrency } from '../../utils/formatters';

const ConfirmationPage = () => {
  const history = useHistory();
  const { currentOrder, loading } = useSelector(state => state.order);
  
  const allOrderItems = useMemo(() => {
    if (!currentOrder || !currentOrder.fulfillmentGroups || currentOrder.fulfillmentGroups.length === 0) {
      return [];
    }
    return currentOrder.fulfillmentGroups.reduce((acc, group) => {
      if (group.orderItems && group.orderItems.length > 0) {
        acc.push(...group.orderItems);
      }
      return acc;
    }, []);
  }, [currentOrder]);
  
  const involvesPickup = useMemo(() => 
    currentOrder?.orderItems?.some(item => item.deliveryMethod === 'pickup'), 
    [currentOrder?.orderItems]
  );
  
  useEffect(() => {
    // If no order data, redirect to home
    if (!loading && !currentOrder) {
      console.log('No current order found, redirecting...');
      history.push('/');
    }
  }, [currentOrder, loading, history]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentOrder) {
    console.log('Current order is null, rendering null');
    return null; // Handled by useEffect redirect
  }

  console.log('Rendering confirmation page with order:', currentOrder);

  const { 
    orderNumber, 
    total, 
    shippingAddress, // This is now an object (or ID) from the backend
    created_at, 
    orderType,       // Added field from backend
    pickupLocation,  // Added field
    estimatedDeliveryDate // Now passed from CheckoutPage
  } = currentOrder;
  
  const orderDate = created_at ? new Date(created_at).toLocaleDateString() : 'Date Unknown';
  
  // Use the estimatedDeliveryDate from currentOrder, provide fallback
  const displayEstimatedDeliveryDate = estimatedDeliveryDate || 'To be determined'; 

  // Helper function to parse variant info
  const parseVariantInfo = (variantString) => {
    try {
      const info = JSON.parse(variantString);
      let parts = [];
      if (info.size) parts.push(`Size: ${info.size}`);
      if (info.color) parts.push(`Color: ${info.color}`);
      // Add other options if they exist in the JSON
      Object.keys(info).forEach(key => {
        if (key !== 'size' && key !== 'color') {
          parts.push(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${info[key]}`);
        }
      });
      return parts.join(', ');
    } catch (e) {
      return variantString; // Return original string if parsing fails
    }
  };
  
  console.log('[ConfirmationPage] Final currentOrder data:', JSON.stringify(currentOrder, null, 2)); // Log the order data

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold">Order Confirmed!</h1>
          <p className="text-gray-600 mt-2">Thank you for your order.</p>
          <p className="text-sm text-gray-500 mt-1">Type: {orderType === 'rental' ? 'Rental' : 'Purchase'}</p>
        </div>
        
        <div className="border-b pb-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold">Order #{orderNumber}</h2>
            <span className="text-gray-600">Placed on {orderDate}</span>
          </div>
        </div>
        
        <div className="mb-6">
          <h3 className="font-semibold mb-3">Order Summary:</h3>
          <div className="space-y-3">
            {/* Use allOrderItems collected from fulfillmentGroups */}
            {allOrderItems && allOrderItems.map((item, index) => {
              const product = item.product || {}; 
              const imageUrl = getProductImageUrl(item); 
              const variantText = parseVariantInfo(item.variantInfo);
              
              return (
                <div key={item.id || index} className="flex items-center">
                  <div className="h-16 w-16 mr-4 overflow-hidden rounded">
                    <img 
                      src={imageUrl} 
                      alt={product.name || 'Product Image'} 
                      className="h-full w-full object-cover"
                      onError={(e) => { e.target.onerror = null; e.target.src='/path/to/default/image.png'; }} // Add image error fallback
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{product.name || 'Product Name'}</p>
                    {variantText && <p className="text-sm text-gray-600">{variantText}</p>}
                    <div className="flex justify-between">
                      <p className="text-sm">Qty: {item.quantity}</p>
                      <p className="font-medium">{formatCurrency(item.price)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="border-t pt-4">
          <div className="flex justify-between font-semibold text-lg mb-6">
            <span>Total:</span>
            <span>{formatCurrency(total)}</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link 
              to="/dashboard/transactions" 
              className="py-2 px-6 border border-brand-orange text-brand-orange rounded-md hover:bg-brand-orange/10 font-medium text-center hover:text-brand-orange transition-colors"
            >
              View Order Details
            </Link>
            <Link 
              to="/browse" 
              className="bg-brand-teal text-white py-2 px-6 rounded-md hover:bg-brand-teal-dark font-medium hover:text-white text-center transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage; 